import React from 'react';
import { Link } from 'react-router-dom';
import { useGuidesBase } from '../../hooks/useGuidesBase';
import { Box, Title, Paper, Text, Stack, Anchor, Alert, Breadcrumbs, List } from '@mantine/core';
import { Lightbulb, Warning } from '@phosphor-icons/react';
import SEO from '../../components/SEO';

export default function MetaAdsSetupGuide() {
  const guidesBase = useGuidesBase();
  return (
    <Box px="lg" py="md">
      <SEO
        title="Konfiguracja Meta Ads (Facebook + Instagram)"
        description="Jak połączyć konto Meta Ads z Droid, wybrać konto reklamowe i stronę Facebook. Krok po kroku."
        path={`${guidesBase}/meta-ads-setup`}
        noIndex={guidesBase !== '/poradniki'}
      />
      <Breadcrumbs mb="sm" styles={{ separator: { color: 'var(--mantine-color-gray-4)' } }}>
        <Anchor component={Link} to={guidesBase} size="sm">Przewodniki</Anchor>
        <Text size="sm" c="dark">Konfiguracja Meta Ads</Text>
      </Breadcrumbs>
      <Title order={3} mb="md">Konfiguracja Meta Ads</Title>

      <Alert color="blue" mb="md" icon={<Lightbulb size={16} />}>
        <Text size="sm">Droid obsługuje kampanie na Facebooku i Instagramie przez Meta Marketing API. Aby rozpocząć, potrzebujesz konta reklamowego Meta i strony Facebook (Fan Page).</Text>
      </Alert>

      <Stack gap="md">
        <Paper withBorder p="lg" radius="md">
          <Text size="sm" fw={600} mb="sm">1. Utwórz konto reklamowe Meta</Text>
          <Text size="sm" c="dimmed" lh={1.7}>
            Jeśli nie masz jeszcze konta reklamowego, przejdź do <Anchor href="https://business.facebook.com" target="_blank">Meta Business Suite</Anchor> i utwórz Business Portfolio.
            W ustawieniach firmy utwórz nowe konto reklamowe. Wybierz walutę (PLN lub USD) i strefę czasową (Europa/Warszawa).
          </Text>
        </Paper>

        <Paper withBorder p="lg" radius="md">
          <Text size="sm" fw={600} mb="sm">2. Utwórz stronę Facebook</Text>
          <Text size="sm" c="dimmed" lh={1.7}>
            Meta wymaga strony Facebook (Fan Page) do publikowania reklam. Każda reklama jest wyświetlana w imieniu strony.
          </Text>
          <List size="sm" mt="xs" spacing="xs">
            <List.Item>Przejdź do <Anchor href="https://facebook.com/pages/create" target="_blank">facebook.com/pages/create</Anchor></List.Item>
            <List.Item>Wybierz kategorię (np. "Firma" lub "Marka")</List.Item>
            <List.Item>Dodaj nazwę, opis i zdjęcie profilowe</List.Item>
            <List.Item>Przypisz stronę do konta reklamowego w Meta Business Suite</List.Item>
          </List>
        </Paper>

        <Paper withBorder p="lg" radius="md">
          <Text size="sm" fw={600} mb="sm">3. Połącz konto Meta z Droid</Text>
          <List size="sm" spacing="xs">
            <List.Item>Zaloguj się do Droid i przejdź do Ustawienia &gt; Platformy &gt; Meta Ads</List.Item>
            <List.Item>Kliknij "Połącz z Meta Ads"</List.Item>
            <List.Item>Zaloguj się kontem Facebook i zaakceptuj uprawnienia</List.Item>
            <List.Item>Po powrocie do Droid, kliknij "Zmień konto" i wybierz konto reklamowe</List.Item>
          </List>
        </Paper>

        <Paper withBorder p="lg" radius="md">
          <Text size="sm" fw={600} mb="sm">4. Dodaj metodę płatności</Text>
          <Text size="sm" c="dimmed" lh={1.7}>
            Bez metody płatności Meta nie będzie wyświetlać reklam. W Meta Business Suite przejdź do Centrum rozliczeń i płatności i dodaj kartę kredytową lub konto bankowe.
          </Text>
          <Alert color="yellow" mt="sm" icon={<Warning size={16} />}>
            <Text size="sm">Jeśli publikacja kampanii z Droid zakończy się błędem "Zaktualizuj metodę płatności", sprawdź ustawienia rozliczeń w Meta Business Suite.</Text>
          </Alert>
        </Paper>

        <Paper withBorder p="lg" radius="md">
          <Text size="sm" fw={600} mb="sm">5. Utwórz pierwszą kampanię</Text>
          <List size="sm" spacing="xs">
            <List.Item>W Droid kliknij "Utwórz kampanię" i wybierz platformę Meta Ads</List.Item>
            <List.Item>Podaj adres strony internetowej, którą chcesz promować</List.Item>
            <List.Item>Agent AI przeanalizuje stronę i zaproponuje grupę docelową, budżet i treści reklam</List.Item>
            <List.Item>Przejrzyj i dostosuj sugestie, wybierz obrazy i stronę Facebook</List.Item>
            <List.Item>Kliknij "Opublikuj" i gotowe!</List.Item>
          </List>
        </Paper>
      </Stack>
    </Box>
  );
}
